import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { noticeApi, AdminNoticeItem, CreateNoticeRequest, UpdateNoticeRequest } from '../../api/notice.api';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Modal, ModalHeader, ModalTitle, ModalContent } from '../ui/modal';
import { Pencil, Trash2, Plus, Check, X } from 'lucide-react';

export const NoticeManagement = () => {
  const queryClient = useQueryClient();

  // 모달 편집 상태
  const [editingItem, setEditingItem] = useState<AdminNoticeItem | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');

  // 새 공지 추가 상태
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');

  /** API */
  const { data: notices = [], isLoading } = useQuery({
    queryKey: ['adminNotices'],
    queryFn: () => noticeApi.getAll().then(res => (res.data as any).results ?? res.data),
  });

  const createMutation = useMutation({
    mutationFn: (params: CreateNoticeRequest) => noticeApi.create(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminNotices'] });
      resetAddForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, params }: { id: number; params: UpdateNoticeRequest }) => noticeApi.update(id, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminNotices'] });
      setEditingItem(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => noticeApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminNotices'] });
    },
  });

  /** 활성/비활성 토글 */
  const toggleActive = (item: AdminNoticeItem) => {
    updateMutation.mutate({ id: item.id, params: { isActive: !item.isActive } });
  };

  /** 모달 편집 시작 */
  const startEdit = (item: AdminNoticeItem) => {
    setEditingItem(item);
    setEditTitle(item.title);
    setEditContent(item.content);
  };

  /** 모달 편집 저장 */
  const saveEdit = () => {
    if (!editingItem) return;
    updateMutation.mutate({
      id: editingItem.id,
      params: { title: editTitle, content: editContent },
    });
  };

  /** 폼 초기화 */
  const resetAddForm = () => {
    setShowAddForm(false);
    setNewTitle('');
    setNewContent('');
  };

  /** 새 공지 저장 */
  const handleCreate = () => {
    if (!newTitle.trim() || !newContent.trim()) return;
    createMutation.mutate({
      title: newTitle.trim(),
      content: newContent.trim(),
      isActive: true,
    });
  };

  /** 삭제 확인 */
  const handleDelete = (id: number) => {
    if (window.confirm('이 공지사항을 삭제하시겠습니까?')) {
      deleteMutation.mutate(id);
    }
  };

  /** 날짜 포맷 */
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="space-y-4">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">공지사항 관리</h2>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-1.5"
          size="sm"
        >
          <Plus className="w-4 h-4" />
          새 공지 추가
        </Button>
      </div>

      {/* 새 공지 추가 폼 */}
      {showAddForm && (
        <Card className="p-4">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700 w-16">제목</label>
              <input
                type="text"
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                placeholder="공지사항 제목"
                className="border rounded-md px-3 py-1.5 text-sm flex-1"
              />
            </div>
            <div className="flex items-start gap-3">
              <label className="text-sm font-medium text-gray-700 w-16 mt-1.5">본문</label>
              <textarea
                value={newContent}
                onChange={e => setNewContent(e.target.value)}
                placeholder="공지사항 본문을 입력하세요"
                className="border rounded-md px-3 py-2 text-sm flex-1 resize-none"
                rows={4}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={resetAddForm}>
                <X className="w-4 h-4 mr-1" />
                취소
              </Button>
              <Button size="sm" onClick={handleCreate} disabled={createMutation.isPending}>
                <Check className="w-4 h-4 mr-1" />
                등록
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* 테이블 */}
      <Card>
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">로딩 중...</div>
        ) : notices.length === 0 ? (
          <div className="p-8 text-center text-gray-500">등록된 공지사항이 없습니다.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/50">
                  <th className="px-4 py-3 text-left font-medium text-gray-500 w-16">ID</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 w-48">제목</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">본문</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-500 w-20">활성</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 w-40">등록일</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-500 w-24">액션</th>
                </tr>
              </thead>
              <tbody>
                {notices.map((item: AdminNoticeItem) => (
                  <tr key={item.id} className="border-b hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 text-gray-500">{item.id}</td>
                    <td className="px-4 py-3">
                      <span className="text-gray-900 font-medium">{item.title}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-700 line-clamp-2">{item.content}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Switch
                        checked={item.isActive}
                        onCheckedChange={() => toggleActive(item)}
                      />
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {formatDate(item.regDateTime)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => startEdit(item)}>
                          <Pencil className="w-4 h-4 text-gray-400" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* 편집 모달 */}
      <Modal
        isOpen={!!editingItem}
        onClose={() => setEditingItem(null)}
        className="max-w-2xl"
      >
        <ModalHeader>
          <ModalTitle>공지사항 수정</ModalTitle>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">제목</label>
              <input
                type="text"
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                className="border rounded-md px-3 py-2 text-sm w-full"
                autoFocus
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">본문</label>
              <textarea
                value={editContent}
                onChange={e => setEditContent(e.target.value)}
                className="border rounded-md px-3 py-2 text-sm w-full resize-none"
                rows={10}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button size="sm" variant="outline" onClick={() => setEditingItem(null)}>
                취소
              </Button>
              <Button size="sm" onClick={saveEdit} disabled={updateMutation.isPending}>
                <Check className="w-4 h-4 mr-1" />
                저장
              </Button>
            </div>
          </div>
        </ModalContent>
      </Modal>
    </div>
  );
};
